import React, { ReactElement, ReactNode, useCallback, useMemo } from "react";
import { useAuth } from "@context/auth";

export const PERMISSIONS = {
  editions: {
    virtual: {
      create: "editions.virtual.create",
      retrieve: "editions.virtual.retrieve",
      update: "editions.virtual.update",
      enrollment: "editions.virtual.enrollment",
    },
    physical: {
      create: "editions.physical.create",
      retrieve: "editions.physical.retrieve",
      update: "editions.physical.update",
      enrollment: "editions.physical.enrollment",
    },
  },
  registry: {
    read: "catalogue.virtualregister.read",
    download: "catalogue.virtualregister.download",
    upload: "catalogue.virtualregister.upload",
  },
  enrollment: {
    read: "learning.enrollment.read",
  },
  users: {
    read: "usermgmt.users.read",
  },
} as const;

export type EditionKindPerm = "virtual" | "physical";
export type EditionActionPerm = "create" | "retrieve" | "update" | "enrollment";

export const editionPerm = (
  kind: EditionKindPerm,
  action: EditionActionPerm,
): string => PERMISSIONS.editions[kind][action];

export const usePermissions = () => {
  const { user } = useAuth();

  const granted = useMemo(
    () => new Set<string>(user?.permissions ?? []),
    [user?.permissions],
  );

  const can = useCallback(
    (permission?: string | string[] | null): boolean => {
      if (!permission) return true;
      const list = Array.isArray(permission) ? permission : [permission];
      return list.every((p) => granted.has(p));
    },
    [granted],
  );

  const canAny = useCallback(
    (permission: string[]): boolean => permission.some((p) => granted.has(p)),
    [granted],
  );

  return { can, canAny };
};

interface PermissionGateProps {
  permission?: string | string[] | null;
  anyOf?: string[];
  mode?: "hide" | "disable";
  children: ReactElement;
  fallback?: ReactNode;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  anyOf,
  mode = "hide",
  children,
  fallback = null,
}) => {
  const { can, canAny } = usePermissions();
  const allowed = anyOf ? canAny(anyOf) : can(permission);

  if (allowed) return children;
  if (mode === "disable") {
    return React.cloneElement(children, { disabled: true });
  }
  return <>{fallback}</>;
};
